// React
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Store
import useAuthStore from '../../../store/authStore.js';

// Components
import { VisibilityOffOutlined, VisibilityOutlined, EmailOutlined, LockOutlined, LockResetOutlined, DarkModeOutlined, LightModeOutlined } from '@mui/icons-material';
import { Box, Button, Grid, IconButton, InputAdornment, Paper, TextField, Typography, useMediaQuery } from '@mui/material';
import CustomSnackbar from '../../custom/CustomSnackbar.js';

// Style
import { red } from '@mui/material/colors/index.js';
import { useAppTheme } from '../../../hooks/useAppTheme.js';

// Assets
import logo from '../../../assets/logo_SIMON_R4.png';
import banner from '../../../assets/banner_trans_SIMON.png';

// Page
import PasswordResetNotConfirmed from './PasswordResetNotConfirmed.js';
import LoadingProgress from '../../custom/LoadingProgress.js';

// Other
import { confirmPasswordLink } from '../../../service/httpService/passwordLink/index.js';

const PasswordReset = () => {
	const { changePassword } = useAuthStore();

	const navigate = useNavigate();

	const [searchParams] = useSearchParams();
	const [isLoading, setLoading] = useState(false);
	const [isConfirmed, setConfirmed] = useState(false);
	const [openFail, setOpenFail] = useState(false);
	const [failMessage, setFailMessage] = useState('');
	const [email, setEmail] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [confirmPassword, setConfirmPassword] = useState('');
	const [password, setPassword] = useState('');
	const [passwordLink, setPasswordLink] = useState('');
	const [focusedField, setFocusedField] = useState(null);
	const emailInputRef = useRef(null);
	const passwordInputRef = useRef(null);
	const confirmPasswordInputRef = useRef(null);

	const mobile = useMediaQuery("(max-width:600px)");
	
	// Theme
	const { isDarkMode, toggleDarkMode, colors, darkTheme: activeDarkTheme, lightTheme: activeLightTheme } = useAppTheme();

	const onChangeEmail = (event) => {
		setEmail(String(event.target.value).toLocaleLowerCase());
	};

	const handleMouseDownPassword = (event) => {
		if (event.button === 0) event.preventDefault();
	};

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleKeyDown = (event) => {
		if (event.key === 'Enter') {
			doResetPassword();
		}
	};

	const doResetPassword = () => {
		if (email === '') {
			setFailMessage('Eingabefehler! (Email)');
			setOpenFail(true);
			return;
		}

		if (password !== confirmPassword) {
			setFailMessage('Passwörter stimmen nicht überein!');
			setOpenFail(true);
			return;
		}

		if (password.length < 8) {
			setFailMessage('Passwort zu kurz! (8 Zeichen)');
			setOpenFail(true);
			return;
		}

		changePassword(email, passwordLink, password).then(res => {
			if (res.error) {
				setFailMessage(res.message);
				setOpenFail(true);
				return;
			}
			navigate('/');
		});
	};

	useEffect(() => {
		confirmPasswordLink(searchParams.get('passwordLink'))
			.then(response => {
				setLoading(false);
				if (response.data.confirm) {
					setConfirmed(true);
					setPasswordLink(searchParams.get('passwordLink'));
					return;
				}
				setConfirmed(false);
			})
				.catch(() => {
					setFailMessage('Fehler beim Laden der Seite!');
					setOpenFail(true);
				});
	}, [searchParams]);

	// Sync autofilled values (password managers) into React state
	useEffect(() => {
		const syncFromDom = () => {
			const domEmail = emailInputRef.current?.value;
			const domPassword = passwordInputRef.current?.value;
			const domConfirm = confirmPasswordInputRef.current?.value;

			if (typeof domEmail === 'string' && domEmail !== '' && domEmail !== email) {
				setEmail(domEmail.toLocaleLowerCase());
			}
			if (typeof domPassword === 'string' && domPassword !== '' && domPassword !== password) {
				setPassword(domPassword);
			}
			if (typeof domConfirm === 'string' && domConfirm !== '' && domConfirm !== confirmPassword) {
				setConfirmPassword(domConfirm);
			}
		};

		let tries = 0;
		syncFromDom();
		const id = setInterval(() => {
			syncFromDom();
			tries += 1;
			if (tries >= 10) clearInterval(id);
		}, 200);
		return () => clearInterval(id);
	}, [email, password, confirmPassword]);

	if (isLoading) {
		return (<LoadingProgress />);
	}

	if (!isConfirmed) {
		return (<PasswordResetNotConfirmed setOpenFail={ setOpenFail } />);
	}

	const inputStyle = (fieldName, hasError = false) => ({
		'& .MuiOutlinedInput-root': {
			borderRadius: 3,
			backgroundColor: colors.login.input.bg,
			'& fieldset': {
				borderColor: hasError ? red[600] : (focusedField === fieldName ? colors.login.input.borderFocus : colors.login.input.border),
			},
			'&:hover fieldset': {
				borderColor: hasError ? red[600] : (focusedField === fieldName ? colors.login.input.borderFocus : colors.login.input.borderHover),
			},
			'&.Mui-focused fieldset': {
				borderColor: hasError ? red[600] : colors.login.input.borderFocus,
			},
			'&:has(input:-webkit-autofill)': {
				backgroundColor: colors.login.input.bg,
			},
			'&:has(input:-webkit-autofill:hover)': {
				backgroundColor: colors.login.input.bgHover,
			},
			'&:has(input:-webkit-autofill:focus)': {
				backgroundColor: colors.login.input.bgFocus,
			},
		}
	});

	const autofillInputSx = {
		'& input:-webkit-autofill': {
			WebkitBoxShadow: '0 0 0 100px transparent inset !important',
			WebkitTextFillColor: 'inherit !important',
			transition: 'background-color 5000s ease-in-out 0s',
		},
	};

	const getLabelProps = (fieldName, fieldValue, hasError = false) => ({
		sx: {
			color: hasError ? red[600] : (fieldValue ? colors.login.input.text : colors.login.input.label),
			'&.Mui-focused': {
				color: hasError ? `${red[600]} !important` : `${colors.login.input.borderFocus} !important`,
			},
			'&.Mui-error': {
				color: `${red[600]} !important`,
			}
		}
	});

	const getInputProps = (startIcon, endAdornment = null) => ({
		startAdornment: startIcon,
		endAdornment,
		sx: {
			color: colors.login.input.text,
			'& .MuiSvgIcon-root': {
				color: colors.login.input.icon,
				mr: 1
			}
		}
	});

	const formContent = (
		<>
			<Typography variant={mobile ? "h6" : "h5"} fontWeight={600} sx={{ mb: 1, textAlign: 'center', color: isDarkMode ? 'white' : 'text.primary' }}>
				Passwort zurücksetzen
			</Typography>
			
			<Typography variant="body2" sx={{ mb: mobile ? 2 : 4, textAlign: 'center', color: isDarkMode ? 'white' : 'text.primary' }}>
				Bitte gib deine neue Anmeldedaten ein
			</Typography>

			<Grid container spacing={2} sx={{ mb: 3 }}>
				<Grid item xs={12}>
					<TextField 
						fullWidth
						variant="outlined" 
						type="email" 
						label="E-Mail" 
						name="email"
						autoComplete="username"
						value={ email } 
						onChange={ onChangeEmail }
						onKeyDown={ handleKeyDown }
						onFocus={ () => setFocusedField('email') }
						onBlur={ () => setFocusedField(null) }
						inputRef={ emailInputRef }
						InputLabelProps={getLabelProps('email', email)}
						InputProps={getInputProps(<EmailOutlined />)}
						sx={{ ...inputStyle('email'), ...autofillInputSx }}
					/>
				</Grid>

				<Grid item xs={12}>
					<TextField 
						fullWidth
						variant="outlined" 
						type={ showPassword ? 'text' : 'password' } 
						label="Neues Passwort" 
						name="newPassword"
						autoComplete="new-password"
						value={ password } 
						onChange={ (event) => setPassword(event.target.value) }
						onKeyDown={ handleKeyDown }
						onFocus={ () => setFocusedField('password') }
						onBlur={ () => setFocusedField(null) }
						inputRef={ passwordInputRef }
						InputLabelProps={getLabelProps('password', password)}
						InputProps={getInputProps(<LockOutlined />, (
							<InputAdornment position="end">
								<IconButton 
									onClick={ handleClickShowPassword } 
									onMouseDown={ handleMouseDownPassword } 
									edge="end"
									sx={{ color: colors.login.input.icon }}
								>
									{ showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined /> }
								</IconButton>
							</InputAdornment>
						))}
						sx={{ ...inputStyle('password'), ...autofillInputSx }}
					/>
				</Grid>

				<Grid item xs={12}>
					<TextField 
						fullWidth
						variant="outlined" 
						type={ showPassword ? 'text' : 'password' } 
						label="Passwort bestätigen" 
						name="confirmPassword"
						autoComplete="new-password"
						value={ confirmPassword } 
						onChange={ (event) => setConfirmPassword(event.target.value) }
						onKeyDown={ handleKeyDown }
						onFocus={ () => setFocusedField('confirmPassword') }
						onBlur={ () => setFocusedField(null) }
						inputRef={ confirmPasswordInputRef }
						InputLabelProps={getLabelProps('confirmPassword', confirmPassword)}
						InputProps={getInputProps(<LockOutlined />, (
							<InputAdornment position="end">
								<IconButton 
									onClick={ handleClickShowPassword } 
									onMouseDown={ handleMouseDownPassword } 
									edge="end"
									sx={{ color: colors.login.input.icon }}
								>
									{ showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined /> }
								</IconButton>
							</InputAdornment>
						))}
						sx={{ ...inputStyle('confirmPassword'), ...autofillInputSx }}
					/>
				</Grid>
			</Grid>

			<Box sx={{ display: 'flex', justifyContent: 'center' }}>
				<Button 
					variant="contained" 
					onClick={ doResetPassword } 
					disabled={ !email || !password || !confirmPassword }
					startIcon={ <LockResetOutlined /> }
					fullWidth
					sx={{ 
						maxWidth: mobile ? '100%' : '75%',
						py: 1.3,
						color: 'white', 
						backgroundColor: colors.login.button.bg, 
						borderRadius: 2,
						textTransform: 'none',
						fontSize: '1rem',
						fontWeight: 600,
						boxShadow: colors.login.button.shadow,
						'&:hover': { 
							backgroundColor: colors.login.button.hover,
							boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
							transform: 'translateY(-1px)',
							transition: 'all 0.3s ease'
						},
						'&:active': {
							transform: 'translateY(0)',
						},
						'&.Mui-disabled': {
							backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
							color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
						}
					}}
				>
					Passwort ändern
				</Button>
			</Box>
		</>
	);

	return (
		<Box sx={{ 
			minHeight: '100vh',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			p: 2,
			background: colors.login.bg,
			backgroundSize: 'cover',
			backgroundPosition: 'center',
			backgroundRepeat: 'no-repeat',
			position: 'relative',
			overflow: 'hidden',
			transition: 'background 0.3s ease',
		}}>
			
			{/* Dark mode blur layer */}
			<Box sx={{
				position: 'absolute',
				top: '-100px', left: '-100px', right: '-100px', bottom: '-100px',
				background: activeDarkTheme.login.blur,
				filter: 'blur(40px)',
				backgroundBlendMode: 'screen, screen, normal',
				zIndex: 0,
				opacity: isDarkMode ? 1 : 0,
				visibility: isDarkMode ? 'visible' : 'hidden',
				transition: 'opacity 0.3s ease',
				pointerEvents: 'none',
			}} />
			
			{/* Light mode blur layer */}
			<Box sx={{
				position: 'absolute',
				top: '-100px', left: '-100px', right: '-100px', bottom: '-100px',
				background: activeLightTheme.login.blur,
				filter: 'blur(50px)',
				zIndex: 0,
				opacity: isDarkMode ? 0 : 1,
				visibility: isDarkMode ? 'hidden' : 'visible',
				transition: 'opacity 0.3s ease',
				pointerEvents: 'none',
			}} />

			{/* Noise overlay */}
			<Box sx={{
				position: 'absolute',
				top: 0, left: 0, right: 0, bottom: 0,
				pointerEvents: 'none',
				backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='${isDarkMode ? '0.03' : '0.02'}'/%3E%3C/svg%3E")`,
				zIndex: 1,
			}} />

			<CustomSnackbar 
				open={openFail} 
				onClose={() => setOpenFail(false)} 
				message={failMessage} 
				severity="error" 
				autoHideDuration={4000}
			/>

			{mobile ? (
				<Paper 
					sx={{ 
						mt: -16,
						width: '90%',
						maxWidth: '400px',
						minHeight: 'auto',
						borderRadius: 4,
						position: 'relative',
						zIndex: 1,
						backgroundColor: isDarkMode ? 'rgba(20, 20, 20, 0.5)' : 'rgba(255, 255, 255, 0.3)',
						border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.3)',
						boxShadow: isDarkMode ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)' : '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
						p: 3,
						transition: 'background-color 0.2s ease, border-color 0.2s ease',
					}}
				>
					<Box sx={{ 
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: 2,
						minHeight: '100%',
					}}>
						{/* Dark Mode Toggle Button (Mobile) */}
						<IconButton
							onClick={toggleDarkMode}
							sx={{
								position: 'absolute',
								top: 16,
								right: 16,
								zIndex: 10,
								backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.6)' : 'rgba(0, 0, 0, 0.06)',
								color: isDarkMode ? colors.accentLight : 'text.secondary',
								border: '1px solid',
								borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
								boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
								'&:hover': {
									backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.8)' : 'rgba(0, 0, 0, 0.1)',
									transform: 'scale(1.05)',
								},
								transition: 'background-color 0.2s ease, transform 0.2s ease',
							}}
						>
							{isDarkMode ? <LightModeOutlined /> : <DarkModeOutlined />}
						</IconButton>

						{/* Logo and Banner */}
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, width: '100%', mb: 1 }}>
							<img alt="logo" style={{ width: 90, height: 90 }} src={ logo }/>
							<img alt="banner" style={{ width: '80%', maxWidth: 280, filter: isDarkMode ? 'invert(1) hue-rotate(180deg) saturate(2)' : 'none' }} src={ banner }/>
						</Box>

						{formContent}
					</Box>
				</Paper>
			) : (
				// Desktop View - Split Screen Design
				<Paper 
					sx={{ 
						width: '100%',
						maxWidth: '1000px',
						minHeight: '550px',
						maxHeight: '90vh',
						overflow: 'hidden',
						borderRadius: 7,
						display: 'flex',
						flexDirection: 'row',
						position: 'relative',
						zIndex: 1,
						backgroundColor: isDarkMode ? 'transparent' : 'transparent',
						backdropFilter: 'none',
						border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
						boxShadow: isDarkMode ? '0 20px 60px rgba(0, 0, 0, 0.5)' : '0 20px 60px rgba(0, 0, 0, 0.15)',
						transition: 'background-color 0.2s ease',
					}}
				>
					{/* Left Side - Banner and Logo */}
					<Box 
						sx={{ 
							width: '50%',
							background: isDarkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
							p: 4,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 6,
							zIndex: 2,
							borderRight: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
							boxShadow: isDarkMode 
								? '8px 0 25px rgba(0, 0, 0, 0.4), inset -1px 0 0 rgba(255, 255, 255, 0.05)' 
								: '8px 0 30px rgba(0, 0, 0, 0.12), inset -1px 0 0 rgba(255, 255, 255, 0.5)',
							transition: 'background-color 0.2s ease, border-color 0.2s ease',
						}}
					>
						<img alt="banner" style={{ width: '90%', maxWidth: 400, filter: isDarkMode ? 'invert(1) hue-rotate(180deg) saturate(2)' : 'none' }} src={ banner }/>
						<img alt="logo" style={{ width: 200, height: 200 }} src={ logo }/>
					</Box>

					{/* Right Side - Reset Form */}
					<Box 
						sx={{ 
							width: '50%',
							p: 4,
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							overflow: 'auto',
							backgroundColor: isDarkMode ? 'rgba(20, 20, 20, 0.4)' : 'rgba(255, 255, 255, 0.3)',
							border: isDarkMode ? 'none' : '1px solid rgba(20, 20, 20, 0.08)',
							borderLeft: 'none',
							borderRadius: isDarkMode ? 0 : '0 28px 28px 0',
							boxShadow: isDarkMode 
								? 'inset 3px 0 15px rgba(0, 0, 0, 0.3)' 
								: 'inset 3px 0 15px rgba(0, 0, 0, 0.10)',
							position: 'relative',
						}}
					>
						{/* Dark Mode Toggle Button (Desktop) */}
						<IconButton
							onClick={toggleDarkMode}
							sx={{
								position: 'absolute',
								top: 24,
								right: 24,
								zIndex: 10,
								backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
								color: isDarkMode ? colors.accentLight : 'text.secondary',
								border: '1px solid',
								borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
								boxShadow: 'none',
								'&:hover': {
									backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
									transform: 'scale(1.05)',
								},
								transition: 'background-color 0.2s ease, transform 0.2s ease',
							}}
						>
							{isDarkMode ? <LightModeOutlined /> : <DarkModeOutlined />}
						</IconButton>

						{formContent}
					</Box>
				</Paper>
			)}
		</Box>
	);
}

export default PasswordReset;